import { useEffect, useState } from 'react';
import { useMarkets } from '@/state/markets';
import { getTicker } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { priceFmt } from '@/lib/format';
import { seedFor } from '@/engine/clock';
import { prngFromSeed } from '@/engine/prng';
import { cn } from '@/lib/cn';

const LEVELS = 7;

export function OrderBook() {
  const tickerId = useMarkets((s) => s.activeTickerId);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const cfg = getTicker(tickerId);
  if (!cfg) return null;
  const mid = priceAt(cfg, now);

  const seed = seedFor(`book:${tickerId}`, now, 500);
  const rng = prngFromSeed(seed);

  const asks: { price: number; size: number }[] = [];
  const bids: { price: number; size: number }[] = [];

  for (let i = 1; i <= LEVELS; i++) {
    const askSpread = 0.0008 * i + rng() * 0.0006;
    const bidSpread = 0.0008 * i + rng() * 0.0006;
    asks.push({ price: mid * (1 + askSpread), size: Math.floor(rng() * 900 + 100) });
    bids.push({ price: mid * (1 - bidSpread), size: Math.floor(rng() * 900 + 100) });
  }

  asks.sort((a, b) => b.price - a.price); // highest first (top of book at bottom)

  const maxSize = Math.max(...asks.map((a) => a.size), ...bids.map((b) => b.size));

  return (
    <div className="flex h-full flex-col bg-[var(--bg-surface)] text-mono-xs">
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-3 py-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
        <span>Depth</span>
        <span>L2</span>
      </div>
      <div className="grid grid-cols-2 gap-px border-b border-[var(--border-subtle)] bg-[var(--border-subtle)] text-[10px] text-[var(--text-muted)]">
        <div className="bg-[var(--bg-surface)] px-3 py-1">BID · SIZE</div>
        <div className="bg-[var(--bg-surface)] px-3 py-1 text-right">ASK · SIZE</div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-2 gap-px bg-[var(--border-subtle)]">
          <div className="bg-[var(--bg-surface)]">
            {bids.map((b, i) => (
              <Row key={i} side="bid" price={b.price} size={b.size} maxSize={maxSize} />
            ))}
          </div>
          <div className="bg-[var(--bg-surface)]">
            {asks.map((a, i) => (
              <Row key={i} side="ask" price={a.price} size={a.size} maxSize={maxSize} />
            ))}
          </div>
        </div>
        <div className="mt-1 border-t border-[var(--border-strong)] px-3 py-1 text-center text-mono-xs text-[var(--text-primary)]">
          mid {priceFmt(mid)}
        </div>
      </div>
    </div>
  );
}

function Row({ side, price, size, maxSize }: { side: 'bid' | 'ask'; price: number; size: number; maxSize: number }) {
  const widthPct = (size / maxSize) * 100;
  const color = side === 'bid' ? 'var(--positive)' : 'var(--negative)';
  return (
    <div className={cn('relative flex justify-between px-3 py-0.5 tabular-nums', side === 'ask' && 'flex-row-reverse')}>
      <div
        className="absolute inset-y-0"
        style={{
          width: `${widthPct}%`,
          background: color,
          opacity: 0.08,
          [side === 'bid' ? 'right' : 'left']: 0,
        }}
      />
      <span style={{ color, position: 'relative' }}>{priceFmt(price)}</span>
      <span className="text-[var(--text-secondary)]" style={{ position: 'relative' }}>{size}</span>
    </div>
  );
}
